/** Writ Book: Receipt Book for Civil Papers Served (SC records retention schedule 12-502.3). */
import { useState } from 'react';
import { api } from '../shared/api.ts';
import { useAuth } from '../shared/auth.tsx';
import { Link } from '../shared/router.tsx';
import { Card, Empty, ErrorBox, Field, Spinner, useAction, useLoad } from '../shared/ui.tsx';

const iso = (tz: string, d = new Date()) => new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
const us = (s: string) => {
  const [y, m, d] = s.split('-');
  return `${m}/${d}/${y}`;
};

export function WritBook() {
  const { me, can } = useAuth();
  const tz = me!.agency.timezone;
  const today = iso(tz);
  const [from, setFrom] = useState(today.slice(0, 8) + '01');
  const [to, setTo] = useState(today);
  const [status, setStatus] = useState('all');
  const [q, setQ] = useState({ from: today.slice(0, 8) + '01', to: today, status: 'all' });
  const r = useLoad(() => api.get<any>(`/api/writ-book?from=${q.from}&to=${q.to}&status=${q.status}`), [q.from, q.to, q.status]);
  const a = useAction();
  const remark = (e: any) => {
    const v = window.prompt(`General remark for ${e.entryNo} (leave blank to clear)`, e.writRemark ?? '');
    if (v === null) return;
    a.run(async () => {
      await api.post(`/api/papers/${e.paperId}/writ-remark`, { remark: v.trim() || null });
      r.reload();
    });
  };
  return (
    <div className="stack writbook">
      <style>{'@page { size: letter landscape; margin: 0.4in; }'}</style>
      <div className="no-print">
        <h1>Writ Book</h1>
        <p className="muted">
          Receipt book for civil papers served (SC records schedule 12-502.3). Every civil paper received, who served it, when and how, and the sheriff’s costs. IV-D and family court papers are
          left off. Keep for 10 years.
        </p>
      </div>
      <Card className="no-print">
        <form
          className="row gap wrap wb-filters"
          onSubmit={(e) => {
            e.preventDefault();
            setQ({ from, to, status });
          }}
        >
          <Field label="Entered from">
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label="Through">
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>
          <Field label="Show">
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="all">All papers</option>
              <option value="served">Served</option>
              <option value="not_served">Not served (non est)</option>
              <option value="pending">Not yet completed</option>
            </select>
          </Field>
          <button className="btn primary">Show</button>
          <span className="spacer" />
          <button type="button" className="btn" onClick={() => window.print()} disabled={!r.data?.entries.length}>
            Print
          </button>
          <a className="btn" href={`/api/writ-book?from=${q.from}&to=${q.to}&status=${q.status}&format=csv`}>
            Download spreadsheet (CSV)
          </a>
        </form>
      </Card>
      <ErrorBox error={r.error ?? a.error} />
      {!r.data ? (
        <Spinner />
      ) : (
        <div className="wb-sheet">
          <div className="wb-head">
            <div>
              <strong>{r.data.agency}</strong>
              {r.data.county ? ` · ${r.data.county}` : ''}
            </div>
            <div className="wb-title">WRIT BOOK: Receipt Book for Civil Papers Served</div>
            <div className="small">
              Entered {us(r.data.from)} – {us(r.data.to)} · {r.data.totals.entries} entr{r.data.totals.entries === 1 ? 'y' : 'ies'} · {r.data.totals.served} served · Sheriff’s costs $
              {(r.data.totals.costsCents / 100).toFixed(2)}
            </div>
            <div className="small muted">SC General Records Retention Schedule 12-502.3 · Retain 10 years · IV-D and family court papers excluded</div>
          </div>
          {r.data.entries.length === 0 ? (
            <Empty>No papers entered in this range.</Empty>
          ) : (
            <div className="table-wrap">
              <table className="table wb-table">
                <thead>
                  <tr>
                    <th>Entry</th>
                    <th>Date of entry</th>
                    <th>Parties</th>
                    <th>Attorney</th>
                    <th>Kind of writ</th>
                    <th>By whom served</th>
                    <th>Date of service</th>
                    <th>Kind of service</th>
                    <th className="num">Sheriff’s costs</th>
                    <th>General remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {r.data.entries.map((e: any) => (
                    <tr key={e.paperId}>
                      <td className="mono">
                        <Link to={`/papers/${e.paperId}`}>{e.entryNo}</Link>
                      </td>
                      <td>{e.dateOfEntry}</td>
                      <td>
                        {e.plaintiff || <span className="muted">—</span>}
                        <div className="small">v. {e.defendant}</div>
                        <div className="small muted">
                          {e.caseNumber}
                          {e.court ? ` · ${e.court}` : ''}
                        </div>
                      </td>
                      <td>{e.attorney}</td>
                      <td>{e.kindOfWrit}</td>
                      <td>{e.servedBy}</td>
                      <td>{e.dateOfService}</td>
                      <td>{e.kindOfService}</td>
                      <td className="num">{e.sheriffsCosts ? `$${e.sheriffsCosts}` : ''}</td>
                      <td>
                        {e.remarks}
                        {can('papers.intake') && (
                          <button className="linkish small no-print" onClick={() => remark(e)} disabled={a.busy}>
                            {e.writRemark ? 'edit' : 'add remark'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
